import moment from 'moment'

// Configure moment calendar locale (ported from the legacy utils module).
moment.locale('en', {
  calendar: {
    lastDay: '[Yesterday at] LT',
    sameDay: '[Today at] LT',
    nextDay: '[Tomorrow at] LT',
    lastWeek: '[last] dddd [at] LT',
    nextWeek: 'dddd [at] LT',
    sameElse: 'dddd DD MMM YYYY',
  },
})

export const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

/** Current local datetime in the storage format. */
export function nowDatetime(): string {
  return moment().format(DATETIME_FORMAT)
}

/**
 * Calories burned via the MET formula:
 *   kcal = MET * 3.5 * weightKg * durationMin / 200
 */
export function calculateCalories(
  durationMinutes: number,
  metValue: number,
  weight: number,
): number {
  const met = Number(metValue)
  const mins = Number(durationMinutes)
  if (met > 0 && mins > 0) {
    return (met * (3.5 * mins * weight)) / 200
  }
  return 0
}

/** Seconds-per-rep tempo used to estimate a resistance set's duration when none is recorded. */
export const RESISTANCE_SECONDS_PER_REP = 3

/** Default MET for weights ("Moderate") — used when neither effort nor a usable load is known. */
export const RESISTANCE_DEFAULT_MET = 5

/** MET tiers for resistance training, matching RESISTANCE_MET_OPTIONS in config/activityForms. */
export const RESISTANCE_MET_TIERS = { light: 3.5, moderate: 5, heavy: 6 } as const

/**
 * Pick a MET tier for a set from the load lifted relative to body weight: under half
 * bodyweight is light, up to bodyweight is moderate, at/above bodyweight is heavy.
 * Falls back to RESISTANCE_DEFAULT_MET when load or body weight is unknown.
 */
export function metForLoad(kg: number, bodyWeight: number): number {
  if (!(kg > 0) || !(bodyWeight > 0)) return RESISTANCE_DEFAULT_MET
  const ratio = kg / bodyWeight
  if (ratio < 0.5) return RESISTANCE_MET_TIERS.light
  if (ratio < 1) return RESISTANCE_MET_TIERS.moderate
  return RESISTANCE_MET_TIERS.heavy
}

/**
 * Estimate calories burned for a resistance (weights) set from reps (or duration) +
 * body weight. Effort is optional: when no MET is given we derive a tier from the load
 * lifted relative to body weight (see metForLoad), so a plain kg + reps entry still gets
 * a figure. Returns null only when there's no time source (reps and duration both empty)
 * or no body weight.
 */
export function estimateWeightsCalories(
  intensity: string | undefined,
  duration: string | undefined,
  reps: string | undefined,
  weight: number,
  kg?: string | undefined,
): number | null {
  const met = Number(intensity) || metForLoad(Number(kg), weight)
  let mins = duration ? timeToSeconds(duration) / 60 : 0
  if (mins === 0) {
    const r = Number(reps ?? '')
    if (r > 0) mins = (r * RESISTANCE_SECONDS_PER_REP) / 60
  }
  if (met > 0 && mins > 0 && weight > 0) {
    return Math.round(calculateCalories(mins, met, weight))
  }
  return null
}

/** Convert "HH:MM:SS" to total seconds. */
export function timeToSeconds(timeStr: string): number {
  const [hours, minutes, seconds] = timeStr.split(':').map(Number)
  return (hours || 0) * 3600 + (minutes || 0) * 60 + (seconds || 0)
}

/** Convert seconds to a "HH:MM:SS" string. */
export function toHHMMSS(seconds: number, decimalPlaces = 0): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds - 3600 * hours) / 60)
  let secs = seconds - 3600 * hours - 60 * minutes
  secs = Math.round(secs * 1000) / 1000

  const days = Math.floor(hours / 24)
  const dayStr = days > 0 ? `${days} day${days > 1 ? 's' : ''}, ` : ''

  const h = hours < 10 ? '0' + hours : '' + hours
  const m = minutes < 10 ? '0' + minutes : '' + minutes
  const s = secs < 10 ? '0' + secs.toFixed(decimalPlaces) : secs.toFixed(decimalPlaces)

  return dayStr + h + ':' + m + ':' + s
}

/** Human-readable description of a date relative to now. */
export function diffHuman(date: string, includeOn = true): string {
  const daysDiff = moment().diff(moment(date), 'days')
  if (daysDiff === 0) return 'Today'
  if (daysDiff === 1) return 'Yesterday'
  return (includeOn ? 'on ' : '') + moment(date).format('ddd, Do MMM YYYY')
}

/** Sort an object's keys descending (newest date first) and return a new object. */
export function sortKeysDesc<T>(obj: Record<string, T>): Record<string, T> {
  const out: Record<string, T> = {}
  for (const key of Object.keys(obj).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0))) {
    out[key] = obj[key]
  }
  return out
}
